import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockClosedIcon, ScaleIcon } from '@heroicons/react/24/outline';

const Security: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl pt-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Security
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Built on EigenLayer's security infrastructure, ensuring computational integrity 
              and decentralized validation for all insurance operations.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">EigenLayer Integration</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Ethereum validator security</li>
              <li>Decentralized validation</li>
              <li>Cryptographic proofs</li>
              <li>Slashing conditions</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <LockClosedIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Smart Contract Security</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Audited contracts</li>
              <li>Automated enforcement</li>
              <li>Transparent operations</li>
              <li>Immutable records</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ScaleIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Governance & Oversight</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Community governance</li>
              <li>Transparent policies</li>
              <li>Regular audits</li>
              <li>Compliance checks</li>
            </ul>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Security Architecture</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Decentralized Validation</h3>
              <p className="text-secondary-600">
                All platform computations are verified by EigenLayer's validator network, 
                ensuring trustless operations and preventing single points of failure.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">2. Smart Contract Security</h3>
              <p className="text-secondary-600">
                Insurance policies are tokenized as NFTs and managed through secure, 
                audited smart contracts with automated claim processing and payouts.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">3. Data Integrity</h3>
              <p className="text-secondary-600">
                All data used for risk assessment and claims processing is validated 
                through multiple independent sources and secured on-chain.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">4. Economic Security</h3>
              <p className="text-secondary-600">
                Validators stake ETH through EigenLayer, creating strong economic 
                incentives for honest behavior and reliable service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Security; 